<template lang="pug">
  
  div
    MHeader(
      page-title="Advertiser Management"
      v-bind:breadcrumbs="breadcrumbs"
    )
    div( v-if="loading" ) Loading, please wait...
    div( v-else class="wrapper wrapper-content" )

      div( class="row d-flex align-items-center" )
        div( class="col-6" )
          strong Filter Advertisers by Name:
          input( class="form-control" placeholder="Filter Results" v-model="filterString" )

        div( class="col-6 text-right" )
          p Page {{ page }} of {{ lastPageNum }}
          span Per page:&nbsp;
          select( v-model="perPage" )
            option( value="10" ) 10
            option( value="25" ) 25
            option( value="50" ) 50
            option( value="100" ) 100
          | &nbsp;
          div( class="btn-group" )
            button( class="btn btn-white" v-bind:disabled="page === 1" v-on:click="() => page -= 1" ) Prev page
            button( class="btn btn-white" v-bind:disabled="page === lastPageNum" v-on:click="() => page += 1" ) Next page
      
      div( class="row" v-if="$moonpull.currentUserIsSuperAdmin()" )
        div( class="col-6" )
          strong Filter Advertisers by Network:
            select( v-model="filterNetwork" )
              option( v-bind:value="undefined" ) Please select
              option( v-for="network in networks" v-bind:value="network.id" ) {{ network.name }}

      div( class="ibox mt-3" )
        div( class="ibox-content" )
          p( class="text-right" )
            button( class="btn btn-outline mr-2" v-on:click="downloadAdvertisers" ) Download Advertisers
            router-link( to="/cms/advertisers/create" class="btn btn-primary" ) Create New Advertiser
          
          table( class="table table-hover mt-4" )
            thead
              tr
                th Network
                th Advertiser Details
                th Tracking Details
                th Cookie Details
                th Comments
                th Actions
            tbody
              template( v-if="$apollo.queries.advertisers.loading" )
                tr
                  td( class="text-center" colspan="6" ) Loading...

              template( v-else-if="! advertisers || ! advertisers.items || advertisers.items.length === 0" )
                tr
                  td( class="text-center" colspan="6" ) No advertisers to display

              template( v-else )
                tr( v-for="advertiser in advertisers.items" )

                  td {{ advertiser.network.name }}

                  td( class="text-left" )
                    p #[strong Name:] {{ advertiser.name }}
                    p #[strong Domain:] {{ advertiser.domain }}
                    p #[strong Prog ID:] {{ advertiser.advertiserId }}

                  td( class="text-left" )
                    p #[strong Primary Tracking Method:] {{ advertiser.primaryTrackingMethod || '-' }}
                    p #[strong Tracking Method Narrative:] {{ advertiser.trackingMethodNarrative || '-' }}
                    p JavaScript: {{ advertiser.javascript }}
                    p S2S: {{ (advertiser.s2s === null) ? 'Not Determined' : (advertiser.s2s) ? 'Y' : 'N' }}
                    p Other: {{ (advertiser.other === null) ? 'Not Determined' : (advertiser.other) ? 'Y' : 'N' }}

                  td( class="text-left" )
                    p #[strong FP Cookie Type:] {{ advertiser.bespokeCookieValueType || '-' }}
                    p #[strong FP Cookie Name:] {{ advertiser.bespokeCookieName || '-' }}
                    p #[strong FP Cookie Value:] {{ advertiser.bespokeCookieValue || '-' }}

                  td( class="text-left" )
                    p( v-if="$moonpull.currentUserIsSuperAdmin()" )
                      strong Moonpull private comment:
                      br
                      span( class="comment" ) {{ advertiser.internalComment }}
                    
                    p
                      strong Public comment:
                      br
                      span( class="comment" ) {{ advertiser.publicComment }}
                  
                  td
                    p
                      router-link( class="btn btn-primary" v-bind:to="`/cms/advertisers/${advertiser.id}/update`" v-bind:disabled="deleting" ) View
                    p
                      button( class="btn btn-danger" v-on:click="deleteAdvertiser(advertiser)" v-bind:disabled="deleting" )
                        span( v-if="deleting" ) Deleting...
                        span( v-else ) Delete

</template>

<style lang="scss" scoped>
.comment {
  white-space: pre-line;
}
</style>

<script>
import Vue from 'vue'
import gql from 'graphql-tag'
export default Vue.extend({
  
  data() {
    return {
      breadcrumbs: [
        { label: 'Home', link: '/' },
        { label: 'Advertisers', link: '/cms/advertisers' }
      ],
      loading: true,
      filterString: '',
      filterNetwork: undefined,
      networks: [],
      fileUploadError: '',
      csv: null,
      networkName: null,
      page: 1,
      perPage: 10,
      deleting: false,
    }
  },

  apollo: {
    advertisers: {
      query: gql`
        query AdvertiserPaginated ($page: Float!, $perPage: Float!, $networkId: ID, $filterString: String) {
          advertiserMany(
            page: $page
            perPage: $perPage
            filter: {
              networkId: {
                equals: $networkId
              }
            }
            filterString: $filterString
          ) {
            count
            items {
              id
              network {
                id
                name
              }
              advertiserId
              name
              domain
              primaryTrackingMethod
              trackingMethodNarrative
              javascript
              s2s
              other
              bespokeCookieName
              bespokeCookieValueType
              bespokeCookieValue
              internalComment
              publicComment
            }
          }
        }
      `,
      variables() {
        return {
          page: Number(this.page),
          perPage: Number(this.perPage),
          filterString: this.filterString,
          networkId: this.filterNetwork,
        }
      },
      update: data => data.advertiserMany,
      debounce: 400
    }
  },

  computed: {

    lastPageNum() {
      return this.advertisers ? Math.ceil(this.advertisers.count / this.perPage) : '...'
    }

  },

  methods: {

    updateCsvFile(e) {
      const file = e.target.files[0]

      if (! file.name.endsWith('.csv')) {
        this.fileUploadError = 'Only .CSV files are supported'
        return
      }

      this.fileUploadError = ''
      this.csv = file
    },

    uploadCsv() {
      const formData = new FormData()
      formData.append('csv', this.csv)
      this.$moonpull.uploadAdvertisers(this.networkName, formData)
        .then(() => {
          this.$store.dispatch('flashMessage', { type: 'success', msg: `Successfully uploaded advertisers. Please refresh the page.` })
        })
    },

    getNetwork(id) {
      return this.networks.find(n => n.id === id)
    },

    async downloadAdvertisers() {
      const advertiserCsvRes = await this.$apollo.query({
        query: gql`query { advertisersCsv }`
      })
      const advertiserCsv = (advertiserCsvRes && advertiserCsvRes.data && advertiserCsvRes.data.advertisersCsv) || 'No advertisers found'
      this.$moonpull.downloadCsv(advertiserCsv, 'advertisers.csv')
    },

    async deleteAdvertiser(advertiser) {
      if (confirm(`This action is irreversible. Are you sure you want to delete "${advertiser.name}"? Note: Existing audits will maintain a cached copy of this advertiser for reference.`)) {
        this.deleting = true
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: ID!) {
            advertiserDeleteById(id: $id)
          }`,
          variables: {
            id: advertiser.id
          }
        })
        this.deleting = false
        this.$apollo.queries.advertisers.refetch()
      }
    },

  },

  async beforeCreate() {
    this.networks = (await this.$moonpull.getNetworks()).filter(n => ! n.aliasNetworkId)
    this.loading = false
  },
})

</script>